import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCopy,
  FiDownload,
  FiFileText,
  FiMapPin,
  FiMoreHorizontal,
  FiRefreshCw,
  FiRss,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../components/Spinner";
import CalendarSubscriptionHelpModal from "../../calendar/CalendarSubscriptionHelpModal";
import { toCalendarSubscriptionUrl } from "../../calendar/calendarSubscriptionUrl";
import { useParentYearPlannerExcel } from "../../calendar/useParentYearPlannerExcel";
import { translateClassName } from "../../../../utils/classroomUtils";

const EXTRA_VISIBLE_EVENTS_STEP = 10;

const getLookupName = (classroomLookup, id) => {
  if (!id && id !== 0) return "";
  const key = String(id);
  if (classroomLookup instanceof Map) return classroomLookup.get(key) || "";
  return classroomLookup?.[key] || "";
};

const getClassroomName = (classroom) =>
  String(
    classroom?.className ||
      classroom?.classroomName ||
      classroom?.name ||
      classroom?.label ||
      classroom?.classroom?.className ||
      classroom?.classroom?.classroomName ||
      classroom?.classroom?.name ||
      classroom?.classroom?.label ||
      "",
  ).trim();

const getTranslatedClassroomName = (classroomOrName) => {
  if (typeof classroomOrName === "string" && classroomOrName.includes("·")) {
    return classroomOrName.trim();
  }

  const translated = translateClassName(classroomOrName);
  if (translated) return translated.trim();

  return typeof classroomOrName === "string"
    ? classroomOrName.trim()
    : getClassroomName(classroomOrName);
};

const stripTeacherTitle = (value) =>
  typeof value === "string"
    ? value
        .trim()
        .replace(/^(juf|meester)\s+/i, "")
        .trim()
    : "";

const getTeacherName = (classroom) => {
  const nested = classroom?.classroom || {};
  const teacher =
    classroom?.teacher ||
    classroom?.primaryTeacher ||
    nested?.teacher ||
    nested?.primaryTeacher ||
    (Array.isArray(classroom?.teachers) ? classroom.teachers[0] : null) ||
    (Array.isArray(nested?.teachers) ? nested.teachers[0] : null);

  const firstName = stripTeacherTitle(
    classroom?.teacherFirstName ||
      nested?.teacherFirstName ||
      teacher?.firstName ||
      teacher?.givenName ||
      teacher?.firstname,
  );

  if (firstName) return firstName;

  return stripTeacherTitle(
    classroom?.teacherName ||
      classroom?.teacherFullName ||
      nested?.teacherName ||
      nested?.teacherFullName ||
      teacher?.displayName ||
      teacher?.fullName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" "),
  );
};

const trimClassroomNumber = (value) =>
  String(value || "")
    .replace(/\s+\d+\s*$/, "")
    .trim();

const getDescriptionField = (description, fieldName) => {
  if (typeof description !== "string") return "";

  const pattern = new RegExp(`^\\s*${fieldName}\\s*:\\s*(.+?)\\s*$`, "im");
  const match = description.match(pattern);
  return match?.[1]?.trim() || "";
};

const getEventClassDisplay = (event, fallbackValue = "") => {
  const fallback = String(fallbackValue || "").trim();
  const classFromDescription = getDescriptionField(event?.description, "Klas");
  const teacherFromDescription = stripTeacherTitle(
    getDescriptionField(event?.description, "Docent"),
  );

  if (classFromDescription && teacherFromDescription) {
    return `${classFromDescription} · ${teacherFromDescription}`;
  }

  if (classFromDescription) return classFromDescription;
  return fallback;
};

const getClassroomCalendarLabel = (classroom) => {
  const classroomName = getTranslatedClassroomName(classroom);
  const teacherName = getTeacherName(classroom);

  if (teacherName && classroomName) {
    return `${classroomName} · ${teacherName}`;
  }

  if (teacherName) return teacherName;
  return classroomName;
};

const getClassroomIds = (classroom) =>
  [
    classroom?.id,
    classroom?.uuid,
    classroom?.classroomId,
    classroom?.classroomUuid,
    classroom?.classroom?.id,
    classroom?.classroom?.uuid,
  ]
    .filter(
      (value) => value !== undefined && value !== null && String(value).trim(),
    )
    .map((value) => String(value));

const getCalendarClassBadges = (event, classroomLookup, fallbackLabel) => {
  const badges = [];
  const seenIds = new Set();

  const addBadge = (id, directName, fallbackName) => {
    const key =
      id !== undefined && id !== null && String(id).trim() ? String(id) : "";
    if (key && seenIds.has(key)) return;
    if (key) seenIds.add(key);

    badges.push({
      key:
        key ||
        `${badges.length}-${directName || fallbackName || fallbackLabel}`,
      label:
        getTranslatedClassroomName(
          directName || getLookupName(classroomLookup, key) || fallbackName,
        ) || fallbackLabel,
    });
  };

  if (Array.isArray(event?.classrooms)) {
    event.classrooms.forEach((classroom) => {
      const ids = getClassroomIds(classroom);
      const name = getClassroomCalendarLabel(classroom);

      if (ids.length) {
        addBadge(
          ids[0],
          name ||
            ids.map((id) => getLookupName(classroomLookup, id)).find(Boolean),
        );
      } else if (name) {
        addBadge("", name);
      }
    });
  }

  const classroomNames = Array.isArray(event?.classroomNames)
    ? event.classroomNames.filter(Boolean)
    : [];

  const classroomIds = [
    ...(Array.isArray(event?.classroomUuids) ? event.classroomUuids : []),
    ...(Array.isArray(event?.classroomIds) ? event.classroomIds : []),
  ];

  classroomIds.forEach((id, index) => addBadge(id, "", classroomNames[index]));

  if (!badges.length && classroomNames.length) {
    classroomNames.forEach((name) => addBadge("", String(name)));
  }

  return badges.length
    ? badges
    : [{ key: "classroom-fallback", label: fallbackLabel }];
};

const getCalendarDateParts = (value, locale = "en-US") => {
  if (!value) return { day: "-", weekday: "" };

  let date = null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    } else {
      date = new Date(value);
    }
  } else {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return { day: "-", weekday: "" };
  }

  return {
    day: String(date.getDate()),
    weekday: date
      .toLocaleDateString(locale, { weekday: "short" })
      .replace(".", "")
      .toLowerCase(),
  };
};

const getCalendarDate = (value) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getNextSchoolYearStart = () => {
  const now = new Date();
  const year = now.getMonth() >= 7 ? now.getFullYear() + 1 : now.getFullYear();
  return new Date(year, 7, 1);
};

const isNextSchoolYearEvent = (event) => {
  const startDate = getCalendarDate(event?.startDate);
  if (!startDate) return false;
  return startDate >= getNextSchoolYearStart();
};

const startOfDayKey = (value) => {
  const date = getCalendarDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const startOfWeekMonday = (date) => {
  const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = (base.getDay() + 6) % 7;
  base.setDate(base.getDate() - weekday);
  return base;
};

const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const getWorkMonthWeeks = (focusDate) => {
  if (!focusDate) return [];

  const monthStart = new Date(focusDate.getFullYear(), focusDate.getMonth(), 1);
  const monthEnd = new Date(
    focusDate.getFullYear(),
    focusDate.getMonth() + 1,
    0,
  );
  const start = startOfWeekMonday(monthStart);
  const end = addDays(startOfWeekMonday(monthEnd), 4);
  const weeks = [];

  let cursor = new Date(start);
  let safety = 0;

  while (cursor <= end && safety < 60) {
    const week = [];
    for (let index = 0; index < 5; index += 1) {
      week.push(addDays(cursor, index));
    }
    weeks.push(week);
    cursor = addDays(cursor, 7);
    safety += 1;
  }

  return weeks;
};

const formatMonthLabel = (date, locale = "en-US") =>
  date
    ? date.toLocaleDateString(locale, { month: "long", year: "numeric" })
    : "";

const formatWindowLabel = (startDate, endDate, locale = "en-US") => {
  if (!startDate || !endDate) return "";

  const sameMonth =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth();

  if (sameMonth) return formatMonthLabel(startDate, locale);

  return `${startDate.toLocaleDateString(locale, { day: "numeric", month: "short" })} - ${endDate.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`;
};

const getMonthValue = (date) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    : "";

const getMonthBounds = (date) => {
  const base = date || new Date();
  return {
    start: new Date(base.getFullYear(), base.getMonth(), 1),
    end: new Date(base.getFullYear(), base.getMonth() + 1, 0),
  };
};

const eventOverlapsMonth = (event, focusDate) => {
  const startDate = getCalendarDate(event?.startDate);
  const endDate = getCalendarDate(event?.endDate) || startDate;
  if (!startDate) return false;

  const { start, end } = getMonthBounds(focusDate);
  return startDate <= end && endDate >= start;
};

const eventOverlapsDay = (event, dayKey) => {
  if (!dayKey) return false;
  const startKey = startOfDayKey(event?.startDate);
  const endKey = startOfDayKey(event?.endDate || event?.startDate);
  if (!startKey) return false;
  return startKey <= dayKey && endKey >= dayKey;
};

const isCalendarEventPast = (event) => {
  const startDate = getCalendarDate(event?.startDate);
  const endDate = getCalendarDate(event?.endDate) || startDate;
  if (!endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventEnd = new Date(endDate);
  eventEnd.setHours(0, 0, 0, 0);

  return eventEnd < today;
};

const getMonthFromValue = (value) => {
  const [year, month] = String(value || "")
    .split("-")
    .map(Number);
  if (!year || !month) return new Date();
  return new Date(year, month - 1, 1);
};

const getWeekEventSegments = (weekDays, allCalendarEvents) => {
  if (!weekDays?.length) return [];

  const weekStart = weekDays[0];
  const weekEnd = weekDays[weekDays.length - 1];

  return allCalendarEvents
    .map((item) => {
      const startDate = getCalendarDate(item.event.startDate);
      const rawEndDate = getCalendarDate(item.event.endDate) || startDate;
      if (!startDate) return null;

      const endDate = rawEndDate >= startDate ? rawEndDate : startDate;
      if (endDate < weekStart || startDate > weekEnd) return null;

      const visibleStart = startDate < weekStart ? weekStart : startDate;
      const visibleEnd = endDate > weekEnd ? weekEnd : endDate;
      const startIndex = weekDays.findIndex(
        (day) => startOfDayKey(day) === startOfDayKey(visibleStart),
      );
      const endIndex = weekDays.findIndex(
        (day) => startOfDayKey(day) === startOfDayKey(visibleEnd),
      );

      if (startIndex < 0 || endIndex < 0) return null;

      return {
        ...item,
        startCol: startIndex + 1,
        span: Math.max(1, endIndex - startIndex + 1),
        continuesBefore: startDate < weekStart,
        continuesAfter: endDate > weekEnd,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.startCol !== right.startCol)
        return left.startCol - right.startCol;
      return right.span - left.span;
    });
};

const CalendarMotionStyles = () => (
  <style>{`
    @keyframes lumenPortalScreenSlideIn {
      from { opacity: 0; transform: translateX(32px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes lumenPortalContentIn {
      from { opacity: 0; transform: translateX(12px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes lumenPortalRowIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .lumen-calendar-screen-in {
      animation: lumenPortalScreenSlideIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    .lumen-calendar-fade-up,
    .lumen-calendar-detail-motion,
    .lumen-calendar-content-in {
      animation: lumenPortalContentIn 260ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    .lumen-calendar-row-in {
      animation: lumenPortalRowIn 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    @media (prefers-reduced-motion: reduce) {
      .lumen-calendar-screen-in,
      .lumen-calendar-fade-up,
      .lumen-calendar-detail-motion,
      .lumen-calendar-content-in,
      .lumen-calendar-row-in {
        animation: none;
      }
    }
  `}</style>
);

const CalendarScopeBadge = ({ isClassEvent, label }) => (
  <span
    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
      isClassEvent
        ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-100"
        : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
    }`}
    title={label}
  >
    {label}
  </span>
);

const CalendarDetailRow = ({ icon: Icon, label, children }) => {
  if (!children) return null;

  return (
    <div className="flex gap-3 rounded-2xl border border-slate-100 bg-white/70 p-3 text-sm shadow-sm dark:border-slate-800 dark:bg-slate-950/35">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-200">
        <Icon aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
          {label}
        </div>
        <div className="mt-1 break-words font-semibold text-slate-800 dark:text-slate-100">
          {children}
        </div>
      </div>
    </div>
  );
};

const CalendarTab = ({
  t,
  calendarLoading,
  calendarError,
  groupedCalendarEvents,
  loadCalendarEvents,
  getCalendarClassNames,
  classroomLookup,
  formatCalendarDate,
  formatCalendarTime,
  schoolCalendarLink,
  classCalendarLinks,
  calendarCopied,
  copyCalendarUrl,
}) => {
  const { i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === "en" || i18n.language?.startsWith("en") ? "en-US" : "nl-NL";
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const {
    download: downloadYearPlannerExcel,
    downloading: yearPlannerExcelDownloading,
    failed: yearPlannerExcelFailed,
  } = useParentYearPlannerExcel();
  const [calendarHelpOpen, setCalendarHelpOpen] = useState(false);
  const [selectedEventKey, setSelectedEventKey] = useState(null);
  const [expandedEventKey, setExpandedEventKey] = useState(null);
  const [calendarFocusDate, setCalendarFocusDate] = useState(() => new Date());
  const [selectedDayKey, setSelectedDayKey] = useState(() =>
    startOfDayKey(new Date()),
  );
  const [visibleExtraEventCount, setVisibleExtraEventCount] = useState(0);
  const calendarMenuRef = useRef(null);
  const eventListRefs = useRef({});

  const subscriptionLinks = useMemo(
    () =>
      [
        {
          key: "school",
          id: "school",
          label: t("parentPortal.calendar.school_agenda", {
            defaultValue: "School calendar",
          }),
          url: schoolCalendarLink,
        },
        ...(classCalendarLinks || []).map((link) => ({
          key: link.id,
          ...link,
          label:
            getClassroomCalendarLabel(link) ||
            getTranslatedClassroomName(link.label) ||
            link.label,
        })),
      ]
        .filter((link) => link.url)
        .map((link) => ({
          ...link,
          subscriptionUrl: toCalendarSubscriptionUrl(link.url),
        })),
    [classCalendarLinks, schoolCalendarLink, t],
  );

  const allCalendarEvents = useMemo(
    () =>
      (groupedCalendarEvents || []).flatMap((group) =>
        (group.events || []).map((event, index) => ({
          event,
          groupKey: group.key,
          eventKey: String(event.uuid || event.id || `${group.key}-${index}`),
        })),
      ),
    [groupedCalendarEvents],
  );

  const totalEventCount = allCalendarEvents.length;
  const focusMonthKey = getMonthValue(calendarFocusDate);

  const nextSchoolYearEvents = useMemo(
    () => allCalendarEvents.filter(({ event }) => isNextSchoolYearEvent(event)),
    [allCalendarEvents],
  );

  const visibleCalendarGroups = useMemo(() => {
    let remainingExtraEvents = visibleExtraEventCount;

    return (groupedCalendarEvents || []).reduce((groups, group) => {
      const events = group.events || [];
      const currentMonthEvents = selectedDayKey
        ? events.filter((event) => eventOverlapsDay(event, selectedDayKey))
        : events.filter((event) =>
            eventOverlapsMonth(event, calendarFocusDate),
          );
      const extraEvents = selectedDayKey
        ? []
        : events.filter(
            (event) => !eventOverlapsMonth(event, calendarFocusDate),
          );
      const visibleExtraEvents =
        remainingExtraEvents > 0
          ? extraEvents.slice(0, remainingExtraEvents)
          : [];

      remainingExtraEvents -= visibleExtraEvents.length;

      const visibleEvents = [...currentMonthEvents, ...visibleExtraEvents];
      if (visibleEvents.length) {
        groups.push({
          ...group,
          events: visibleEvents,
          startsExtraSection:
            currentMonthEvents.length === 0 && visibleExtraEvents.length > 0,
        });
      }
      return groups;
    }, []);
  }, [
    calendarFocusDate,
    groupedCalendarEvents,
    selectedDayKey,
    visibleExtraEventCount,
  ]);

  const hiddenExtraEventCount = useMemo(
    () =>
      selectedDayKey
        ? 0
        : allCalendarEvents.filter(
            ({ event }) => !eventOverlapsMonth(event, calendarFocusDate),
          ).length,
    [allCalendarEvents, calendarFocusDate, selectedDayKey],
  );
  const hasMoreEvents = visibleExtraEventCount < hiddenExtraEventCount;

  const selectedEventMeta = useMemo(() => {
    if (!selectedEventKey || !allCalendarEvents.length) return null;
    return (
      allCalendarEvents.find((item) => item.eventKey === selectedEventKey) ||
      null
    );
  }, [allCalendarEvents, selectedEventKey]);

  useEffect(() => {
    if (selectedEventKey || !allCalendarEvents.length) return;

    const dayKey = selectedDayKey || startOfDayKey(new Date());
    const initialItem = allCalendarEvents.find(({ event }) =>
      eventOverlapsDay(event, dayKey),
    );

    if (initialItem) {
      setSelectedEventKey(initialItem.eventKey);
      setExpandedEventKey(initialItem.eventKey);
    }
  }, [allCalendarEvents, selectedDayKey, selectedEventKey]);

  useEffect(() => {
    setVisibleExtraEventCount(0);
  }, [focusMonthKey]);

  useEffect(() => {
    if (!calendarMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!calendarMenuRef.current?.contains(event.target)) {
        setCalendarMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setCalendarMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [calendarMenuOpen]);

  const handleCopyCalendarUrl = (link) => {
    copyCalendarUrl(link.url, String(link.key || link.id));
  };

  const centerEventInList = (eventKey) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        eventListRefs.current[eventKey]?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      });
    });
  };

  const focusCalendarEvent = (
    eventKey,
    event,
    expand = true,
    syncCalendarMonth = true,
  ) => {
    setSelectedEventKey(eventKey);
    setExpandedEventKey(expand ? eventKey : null);

    const eventDate = getCalendarDate(event.startDate);
    const eventDayKey = startOfDayKey(event.startDate);
    if (eventDayKey) setSelectedDayKey(eventDayKey);

    if (syncCalendarMonth && eventDate) {
      setCalendarFocusDate(eventDate);
    }

    centerEventInList(eventKey);
  };

  const focusCalendarDay = (day) => {
    const dayKey = startOfDayKey(day);
    setSelectedDayKey(dayKey);
    setCalendarFocusDate(day);

    const dayItem = allCalendarEvents.find(({ event }) =>
      eventOverlapsDay(event, dayKey),
    );
    if (dayItem) {
      setSelectedEventKey(dayItem.eventKey);
      setExpandedEventKey(dayItem.eventKey);
      centerEventInList(dayItem.eventKey);
      return;
    }

    setSelectedEventKey(null);
    setExpandedEventKey(null);
  };

  const renderEventBadges = (event) => {
    const isClassEvent =
      String(event.scope || "").toUpperCase() === "CLASSROOM";
    const scopeBadges = isClassEvent
      ? getCalendarClassBadges(
          event,
          classroomLookup,
          t("parentPortal.calendar.scope_class", { defaultValue: "Class" }),
        )
      : [
          {
            key: "school",
            label: t("parentPortal.calendar.scope_school", {
              defaultValue: "School-wide",
            }),
          },
        ];

    return scopeBadges.map((badge) => {
      const label = isClassEvent
        ? getEventClassDisplay(event, badge.label)
        : badge.label;

      return (
        <CalendarScopeBadge
          key={badge.key}
          isClassEvent={isClassEvent}
          label={label}
        />
      );
    });
  };

  const selectedEvent = selectedEventMeta?.event;
  const selectedClassNames = selectedEvent
    ? getCalendarClassNames(selectedEvent)
    : "";
  const selectedIsMultiDay =
    selectedEvent &&
    formatCalendarDate(selectedEvent.startDate) !==
      formatCalendarDate(selectedEvent.endDate);

  const monthWeeks = useMemo(
    () => getWorkMonthWeeks(calendarFocusDate),
    [calendarFocusDate],
  );
  const monthStartDate = monthWeeks[0]?.[0];
  const monthEndDate = monthWeeks[monthWeeks.length - 1]?.[4];

  const weekSegments = useMemo(
    () =>
      monthWeeks.map((week) => getWeekEventSegments(week, allCalendarEvents)),
    [allCalendarEvents, monthWeeks],
  );

  const weekDayLabels = useMemo(() => ["ma", "di", "wo", "do", "vr"], []);

  const monthOptions = useMemo(() => {
    const months = new Map();
    const addMonth = (date) => {
      if (!date) return;
      const monthDate = new Date(date.getFullYear(), date.getMonth(), 1);
      months.set(getMonthValue(monthDate), monthDate);
    };

    addMonth(calendarFocusDate);

    const today = new Date();
    for (let offset = -12; offset <= 12; offset += 1) {
      addMonth(new Date(today.getFullYear(), today.getMonth() + offset, 1));
    }
    allCalendarEvents.forEach(({ event }) => {
      addMonth(getCalendarDate(event.startDate));
      addMonth(getCalendarDate(event.endDate));
    });

    return Array.from(months.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([value, date]) => ({ value, label: formatMonthLabel(date, dateLocale) }));
  }, [allCalendarEvents, calendarFocusDate, dateLocale]);

  const moveCalendarFocus = (monthOffset) => {
    setCalendarFocusDate(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + monthOffset, 1),
    );
    setVisibleExtraEventCount(0);
    setSelectedDayKey(null);
  };

  return (
    <div className="lumen-calendar-screen-in mx-auto w-full max-w-5xl space-y-5 text-left">
      <header className="flex min-h-[88px] flex-col gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm"
            aria-hidden="true"
          >
            <FiCalendar />
          </span>
          <span className="min-w-0">
            <h2 className="truncate text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
              {t("parentPortal.calendar.section_label", {
                defaultValue: "Calendar",
              })}
            </h2>
            <p className="mt-1 truncate text-sm font-medium text-slate-500 dark:text-slate-400">
              {t("parentPortal.calendar.intro", {
                defaultValue: "School activities and class events",
              })}
            </p>
          </span>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:ml-auto">
          <button
            type="button"
            onClick={loadCalendarEvents}
            disabled={calendarLoading}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60 dark:border-emerald-800 dark:bg-slate-900/70 dark:text-emerald-200"
          >
            <FiRefreshCw
              className={calendarLoading ? "animate-spin" : ""}
              aria-hidden="true"
            />
            {calendarLoading
              ? t("common.loading", { defaultValue: "Loading..." })
              : t("common.refresh", { defaultValue: "Refresh" })}
          </button>

          <div ref={calendarMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setCalendarMenuOpen((open) => !open)}
              aria-expanded={calendarMenuOpen}
              aria-label={t("parentPortal.calendar.subscription_menu", {
                defaultValue: "Calendar options",
              })}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-200 bg-white/85 text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-slate-900/70 dark:text-emerald-200"
            >
              <FiMoreHorizontal aria-hidden="true" />
            </button>

            {calendarMenuOpen && (
              <div className="absolute right-0 top-12 z-20 w-80 rounded-2xl border border-white/80 bg-white/95 p-1.5 shadow-xl shadow-emerald-100/50 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
                <div className="max-h-80 overflow-y-auto pr-1">
                  {subscriptionLinks.map((link) => {
                    const copied =
                      calendarCopied === String(link.key || link.id);
                    return (
                      <div
                        key={link.key || link.id}
                        className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition hover:bg-emerald-50/80 dark:hover:bg-emerald-900/30"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-emerald-950 dark:text-emerald-100">
                          {link.label}
                        </span>
                        <a
                          href={link.subscriptionUrl || link.url}
                          aria-label={t(
                            "parentPortal.calendar.add_named_agenda",
                            { name: link.label, defaultValue: "Add {{name}}" },
                          )}
                          title={t("parentPortal.calendar.add_agenda", {
                            defaultValue: "Add calendar",
                          })}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white transition hover:bg-emerald-500"
                        >
                          <FiRss aria-hidden="true" />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleCopyCalendarUrl(link)}
                          aria-label={t(
                            "parentPortal.calendar.copy_named_link",
                            {
                              name: link.label,
                              defaultValue: "Copy link for {{name}}",
                            },
                          )}
                          title={t("parentPortal.calendar.copy_link", {
                            defaultValue: "Copy link",
                          })}
                          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition hover:bg-white dark:border-emerald-900/50 dark:hover:bg-slate-800 ${copied ? "border-emerald-200 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100" : "border-emerald-100 bg-white/75 text-emerald-800 dark:bg-slate-900/75 dark:text-emerald-100"}`}
                        >
                          <FiCopy aria-hidden="true" />
                        </button>
                        <a
                          href={link.url}
                          download
                          aria-label={t(
                            "parentPortal.calendar.download_named_agenda",
                            {
                              name: link.label,
                              defaultValue: "Download {{name}}",
                            },
                          )}
                          title={t("parentPortal.calendar.download_agenda", {
                            defaultValue: "Download calendar",
                          })}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-100 bg-white/75 text-emerald-800 transition hover:bg-white dark:border-emerald-900/50 dark:bg-slate-900/75 dark:text-emerald-100 dark:hover:bg-slate-800"
                        >
                          <FiDownload aria-hidden="true" />
                        </a>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 border-t border-emerald-100/80 px-0.5 pt-1.5 dark:border-emerald-900/50">
                  <button
                    type="button"
                    onClick={downloadYearPlannerExcel}
                    disabled={yearPlannerExcelDownloading}
                    className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm font-semibold text-emerald-950 transition hover:bg-emerald-50/80 disabled:cursor-not-allowed disabled:opacity-60 dark:text-emerald-100 dark:hover:bg-emerald-900/30"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {yearPlannerExcelDownloading
                        ? t("common.working", { defaultValue: "Working..." })
                        : t("parentPortal.calendar.download_year_planner", { defaultValue: "Download year planner" })}
                    </span>
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-100 bg-white/75 text-emerald-800 dark:border-emerald-900/50 dark:bg-slate-900/75 dark:text-emerald-100">
                      <FiFileText aria-hidden="true" />
                    </span>
                  </button>
                  {yearPlannerExcelFailed && (
                    <p role="alert" className="px-2 pb-1 pt-1 text-xs text-rose-700 dark:text-rose-300">
                      {t("parentPortal.calendar.download_year_planner_failed", {
                        defaultValue: "The year planner could not be downloaded. Please try again later.",
                      })}
                    </p>
                  )}
                </div>
                <div className="mt-4 border-t border-emerald-100/80 px-2 pb-1 pt-4 text-sm text-gray-500 dark:border-emerald-900/50 dark:text-slate-400">
                  <span>
                    {t("parentPortal.calendar.subscription_auto_updates", {
                      defaultValue: "Calendars update automatically.",
                    })}
                  </span>{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setCalendarHelpOpen(true);
                      setCalendarMenuOpen(false);
                    }}
                    className="font-medium text-emerald-700 transition hover:text-emerald-900 hover:underline dark:text-emerald-200 dark:hover:text-emerald-100"
                  >
                    {t("parentPortal.calendar.subscription_how_it_works", {
                      defaultValue: "ⓘ How does this work?",
                    })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
        {calendarError && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-100">
            {calendarError}
          </div>
        )}

        {calendarLoading ? (
          <div className="py-12">
            <Spinner theme="parent" />
          </div>
        ) : !groupedCalendarEvents?.length ? (
          <div className="rounded-3xl border border-dashed border-emerald-200 bg-emerald-50/50 px-5 py-10 text-center text-sm text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-100">
            {t("parentPortal.calendar.empty", {
              defaultValue: "There are no upcoming calendar items.",
            })}
          </div>
        ) : (
          <div key={calendarLoading ? "calendar-loading" : "calendar-content"} className="lumen-calendar-content-in grid gap-5 transition-all duration-300 ease-out xl:grid-cols-[minmax(0,1.18fr)_minmax(360px,0.82fr)]">
            <section className="lumen-calendar-fade-up min-w-0">
              <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                    {formatMonthLabel(calendarFocusDate, dateLocale)}
                  </div>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {t("parentPortal.calendar.month_view_hint", {
                      defaultValue:
                        "School days only. Multi-day items are shown as one bar across the week.",
                    })}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => moveCalendarFocus(-1)}
                    aria-label={t("parentPortal.calendar.previous_month", {
                      defaultValue: "Previous month",
                    })}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-emerald-100 bg-white/80 text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-950/50 dark:text-emerald-100"
                  >
                    <FiChevronLeft aria-hidden="true" />
                  </button>
                  <select
                    value={getMonthValue(calendarFocusDate)}
                    onChange={(event) => {
                      setCalendarFocusDate(
                        getMonthFromValue(event.target.value),
                      );
                      setSelectedDayKey(null);
                      setVisibleExtraEventCount(0);
                    }}
                    aria-label={t("parentPortal.calendar.month_picker", {
                      defaultValue: "Choose month",
                    })}
                    className="h-9 rounded-full border border-emerald-100 bg-white/80 px-3 text-xs font-bold uppercase tracking-[0.08em] text-emerald-800 shadow-sm outline-none transition hover:bg-emerald-50 focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-slate-950/50 dark:text-emerald-100"
                  >
                    {monthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => moveCalendarFocus(1)}
                    aria-label={t("parentPortal.calendar.next_month", {
                      defaultValue: "Next month",
                    })}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-emerald-100 bg-white/80 text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-950/50 dark:text-emerald-100"
                  >
                    <FiChevronRight aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCalendarFocusDate(new Date());
                      setSelectedDayKey(startOfDayKey(new Date()));
                      setVisibleExtraEventCount(0);
                    }}
                    className="rounded-full border border-emerald-100 bg-white/80 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-950/50 dark:text-emerald-100"
                  >
                    {t("parentPortal.calendar.today", {
                      defaultValue: "Today",
                    })}
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-[1.5rem] border border-slate-100 bg-white/70 dark:border-slate-800 dark:bg-slate-950/20">
                <div className="grid grid-cols-5 border-b border-slate-100 text-center text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-slate-800 dark:text-slate-500">
                  {weekDayLabels.map((day) => (
                    <div
                      key={day}
                      className="border-r border-slate-100 py-2 last:border-r-0 dark:border-slate-800"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {monthWeeks.map((week, weekIndex) => {
                    const selectedDayColumn = week.findIndex(
                      (day) => startOfDayKey(day) === selectedDayKey,
                    );

                    return (
                      <div
                        key={startOfDayKey(week[0])}
                        className="relative grid grid-cols-5 gap-y-1 border-slate-100 p-2 dark:border-slate-800"
                      >
                        {selectedDayColumn >= 0 && (
                          <div
                            aria-hidden="true"
                            className="pointer-events-none absolute bottom-2 top-2 z-0 rounded-2xl bg-emerald-50/80 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.10)] dark:bg-emerald-950/20"
                            style={{
                              left: `calc(${selectedDayColumn * 20}% + 0.5rem)`,
                              width: "calc(20% - 1rem)",
                            }}
                          />
                        )}

                        {week.map((day) => {
                          const dayKey = startOfDayKey(day);
                          const todayKey = startOfDayKey(new Date());
                          const isToday = dayKey === todayKey;
                          const isPast = dayKey < todayKey;
                          const isOutsideMonth =
                            day.getMonth() !== calendarFocusDate.getMonth();
                          const isSelectedDay = selectedDayKey === dayKey;

                          return (
                            <button
                              key={dayKey}
                              type="button"
                              onClick={() => focusCalendarDay(day)}
                              className={`relative z-10 min-h-[52px] rounded-2xl px-2 py-2 text-left transition hover:-translate-y-0.5 hover:bg-emerald-50/70 ${
                                isToday
                                  ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/25 dark:text-emerald-100"
                                  : isPast
                                    ? "bg-slate-100 text-slate-400 dark:bg-slate-900/60 dark:text-slate-600"
                                    : isOutsideMonth
                                      ? "bg-slate-50/70 text-slate-300 dark:bg-slate-900/30 dark:text-slate-700"
                                      : "text-slate-500 dark:text-slate-400"
                              } ${
                                isSelectedDay
                                  ? "bg-emerald-50/40 dark:bg-emerald-950/20"
                                  : ""
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-xs font-bold">
                                  {day.getDate()}
                                </span>
                                {isToday && (
                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-100">
                                    {t("parentPortal.calendar.today", {
                                      defaultValue: "Today",
                                    })}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}

                        {(weekSegments[weekIndex] || [])
                          .slice(0, 5)
                          .map(
                            ({
                              event,
                              eventKey,
                              startCol,
                              span,
                              continuesBefore,
                              continuesAfter,
                            }) => {
                              const isSelected =
                                selectedEventMeta?.eventKey === eventKey;
                              const isClassEvent =
                                String(event.scope || "").toUpperCase() ===
                                "CLASSROOM";
                              const eventEndKey = startOfDayKey(
                                event.endDate || event.startDate,
                              );
                              const isPastEvent =
                                eventEndKey < startOfDayKey(new Date());
                              const isMultiDay =
                                formatCalendarDate(event.startDate) !==
                                formatCalendarDate(event.endDate);
                              return (
                                <button
                                  key={`${eventKey}-${startCol}-${span}`}
                                  type="button"
                                  onClick={() =>
                                    focusCalendarEvent(eventKey, event, true)
                                  }
                                  style={{
                                    gridColumn: `${startCol} / span ${span}`,
                                  }}
                                  className={`relative z-10 min-w-0 truncate rounded-xl px-2.5 py-1.5 text-left text-xs font-semibold shadow-sm transition hover:-translate-y-0.5 ${
                                    isSelected
                                      ? "bg-emerald-600 text-white"
                                      : isPastEvent
                                        ? "bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
                                        : isClassEvent
                                          ? "bg-sky-100 text-sky-900 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-100"
                                          : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-100"
                                  }`}
                                  title={event.title || ""}
                                >
                                  {continuesBefore ? "‹ " : ""}
                                  {event.title || "-"}
                                  {continuesAfter ? " ›" : ""}
                                </button>
                              );
                            },
                          )}

                        {(weekSegments[weekIndex] || []).length > 5 && (
                          <div className="col-span-5 px-2 text-xs font-semibold text-slate-400 dark:text-slate-500">
                            +{(weekSegments[weekIndex] || []).length - 5}{" "}
                            {t("parentPortal.calendar.more_items", {
                              defaultValue: "more",
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="min-w-0 overflow-hidden space-y-4">
              <div>
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                      {t("parentPortal.calendar.event_list", {
                        defaultValue: "Event list",
                      })}
                    </div>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {t("parentPortal.calendar.event_list_hint", {
                        defaultValue:
                          "Select a day to see its items. Scroll for the rest of the list.",
                      })}
                    </p>
                  </div>
                </div>

                <div className="max-h-[min(680px,calc(100vh-18rem))] space-y-5 overflow-y-auto pr-2 scroll-smooth">
                  {visibleCalendarGroups.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.startsExtraSection &&
                        visibleExtraEventCount > 0 && (
                          <div className="flex items-center gap-3 py-2">
                            <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800" />
                            <button
                              type="button"
                              onClick={() => setVisibleExtraEventCount(0)}
                              className="rounded-full border border-slate-200 bg-white/85 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200"
                            >
                              {t("parentPortal.calendar.show_less", {
                                defaultValue: "Show less",
                              })}
                            </button>
                            <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800" />
                          </div>
                        )}

                      <section className="space-y-2">
                        <div className="px-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                          {group.label}
                        </div>

                        <div className="space-y-3">
                          {group.events.map((event, index) => {
                            const eventKey = String(
                              event.uuid || event.id || `${group.key}-${index}`,
                            );
                            const isSelected =
                              selectedEventMeta?.eventKey === eventKey;
                            const isExpanded = expandedEventKey === eventKey;
                            const startParts = getCalendarDateParts(
                              event.startDate,
                              dateLocale,
                            );
                            const endParts = getCalendarDateParts(
                              event.endDate,
                              dateLocale,
                            );
                            const isMultiDay =
                              formatCalendarDate(event.startDate) !==
                              formatCalendarDate(event.endDate);
                            const isPast = isCalendarEventPast(event);
                            const classNames = getEventClassDisplay(
                              event,
                              getCalendarClassNames(event),
                            );

                            return (
                              <article
                                key={eventKey}
                                ref={(node) => {
                                  if (node)
                                    eventListRefs.current[eventKey] = node;
                                  else delete eventListRefs.current[eventKey];
                                }}
                                style={{
                                  animationDelay: `${Math.min(index * 45, 220)}ms`,
                                }}
                                className={`lumen-calendar-row-in overflow-hidden rounded-3xl border shadow-sm transition-all duration-300 ease-out ${
                                  isSelected
                                    ? "border-emerald-300 bg-emerald-50/80 ring-1 ring-emerald-200 dark:border-emerald-700 dark:bg-emerald-950/30 dark:ring-emerald-900/60"
                                    : isPast
                                      ? "border-slate-100 bg-white/45 opacity-60 shadow-none dark:border-slate-800/70 dark:bg-slate-900/30"
                                      : "border-white/70 bg-white/70 dark:border-slate-800/80 dark:bg-slate-900/55"
                                }`}
                              >
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  onClick={() =>
                                    focusCalendarEvent(
                                      eventKey,
                                      event,
                                      !isExpanded,
                                      false,
                                    )
                                  }
                                  className="group flex w-full items-start gap-3 p-4 text-left transition hover:bg-white/75 dark:hover:bg-slate-900/75"
                                >
                                  <div
                                    className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl border shadow-sm dark:bg-slate-950/70 ${
                                      isPast
                                        ? "border-slate-100 bg-white/70 dark:border-slate-800"
                                        : "border-emerald-100 bg-white dark:border-emerald-900/60"
                                    }`}
                                  >
                                    <div className="text-xl font-bold leading-none text-slate-900 dark:text-white">
                                      {isMultiDay
                                        ? `${startParts.day}–${endParts.day}`
                                        : startParts.day}
                                    </div>
                                    <div className="mt-0.5 text-[11px] font-semibold lowercase leading-none text-slate-500 dark:text-slate-400">
                                      {isMultiDay
                                        ? `${startParts.weekday}–${endParts.weekday}`
                                        : startParts.weekday}
                                    </div>
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <div className="flex min-w-0 items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <div
                                          className={`truncate text-base font-bold ${
                                            isPast
                                              ? "text-slate-500 dark:text-slate-400"
                                              : "text-slate-950 dark:text-white"
                                          }`}
                                        >
                                          {event.title || "-"}
                                        </div>
                                        <div className="mt-2 flex max-w-full flex-wrap items-center gap-2">
                                          {renderEventBadges(event)}
                                          {isNextSchoolYearEvent(event) && (
                                            <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-100">
                                              {t(
                                                "parentPortal.calendar.next_school_year",
                                                {
                                                  defaultValue:
                                                    "Next school year",
                                                },
                                              )}
                                            </span>
                                          )}
                                          <span className="inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-600 transition group-hover:bg-emerald-50 group-hover:text-emerald-800 dark:bg-slate-950/60 dark:text-slate-300 dark:group-hover:bg-emerald-950/50 dark:group-hover:text-emerald-100">
                                            <FiClock
                                              className="shrink-0"
                                              aria-hidden="true"
                                            />
                                            <span className="truncate">
                                              {isMultiDay
                                                ? `${formatCalendarDate(event.startDate)} - ${formatCalendarDate(
                                                    event.endDate,
                                                  )}`
                                                : formatCalendarTime(event)}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <FiChevronDown
                                    className={`mt-5 shrink-0 text-slate-300 transition duration-300 group-hover:text-emerald-500 ${
                                      isExpanded
                                        ? "rotate-180 text-emerald-500"
                                        : ""
                                    }`}
                                    aria-hidden="true"
                                  />
                                </button>

                                <div
                                  className={`grid transition-all duration-300 ease-out ${
                                    isExpanded
                                      ? "grid-rows-[1fr] opacity-100"
                                      : "grid-rows-[0fr] opacity-0"
                                  }`}
                                >
                                  <div className="overflow-hidden">
                                    <div className="space-y-3 border-t border-emerald-100/70 p-4 dark:border-emerald-900/50">
                                      <CalendarDetailRow
                                        icon={FiClock}
                                        label={t("schoolCalendar.fields.date", {
                                          defaultValue: "Date",
                                        })}
                                      >
                                        {isMultiDay
                                          ? `${formatCalendarDate(event.startDate)} - ${formatCalendarDate(
                                              event.endDate,
                                            )}`
                                          : `${formatCalendarDate(event.startDate)} · ${formatCalendarTime(event)}`}
                                      </CalendarDetailRow>

                                      <CalendarDetailRow
                                        icon={FiMapPin}
                                        label={t(
                                          "schoolCalendar.fields.location",
                                          { defaultValue: "Location" },
                                        )}
                                      >
                                        {event.location}
                                      </CalendarDetailRow>

                                      <CalendarDetailRow
                                        icon={FiCalendar}
                                        label={t(
                                          "schoolCalendar.fields.classrooms",
                                          { defaultValue: "Classes" },
                                        )}
                                      >
                                        {classNames}
                                      </CalendarDetailRow>

                                      {event.description && (
                                        <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-5 text-sm leading-6 text-slate-700 dark:border-emerald-900/50 dark:bg-emerald-950/25 dark:text-slate-200">
                                          {event.description}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    </React.Fragment>
                  ))}
                </div>

                {visibleCalendarGroups.length === 0 && (
                  <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900/35 dark:text-slate-400">
                    <div className="font-semibold text-slate-700 dark:text-slate-200">
                      {selectedDayKey === startOfDayKey(new Date())
                        ? t("parentPortal.calendar.no_items_today", {
                            defaultValue: "No items today",
                          })
                        : t("parentPortal.calendar.no_items_this_day", {
                            defaultValue: "No items on this day",
                          })}
                    </div>
                    <p className="mt-1">
                      {t("parentPortal.calendar.no_items_hint", {
                        defaultValue:
                          "Choose another day in the calendar or use the month controls.",
                      })}
                    </p>
                  </div>
                )}

                {hasMoreEvents && (
                  <div className="pt-4 text-center">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleExtraEventCount(
                          (count) => count + EXTRA_VISIBLE_EVENTS_STEP,
                        )
                      }
                      className="rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-slate-900/70 dark:text-emerald-200"
                    >
                      {t("parentPortal.calendar.show_more", {
                        defaultValue: "Show more",
                      })}
                    </button>
                  </div>
                )}
              </div>

              {nextSchoolYearEvents.length > 0 && (
                <div className="lumen-calendar-fade-up rounded-3xl border border-violet-100 bg-violet-50/60 p-4 text-sm text-violet-950 shadow-sm dark:border-violet-900/50 dark:bg-violet-950/25 dark:text-violet-100">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
                    {t("parentPortal.calendar.next_school_year_planning", {
                      defaultValue: "Next school year planning",
                    })}
                  </div>
                  <p className="mt-2 leading-6">
                    {t("parentPortal.calendar.next_school_year_available", {
                      count: nextSchoolYearEvents.length,
                      defaultValue:
                        "Planning for next school year is available in this calendar overview.",
                    })}
                  </p>
                </div>
              )}
            </section>
          </div>
        )}
      </section>
      <CalendarMotionStyles />

      <CalendarSubscriptionHelpModal
        open={calendarHelpOpen}
        onClose={() => setCalendarHelpOpen(false)}
        t={t}
      />
    </div>
  );
};

export default CalendarTab;
